import React, { useState, useEffect, useRef, useCallback } from "react";
import { 
  Card, 
  CardBody, 
  CardHeader, 
  Button, 
  Table, 
  Row, 
  Col, 
  Alert,
  Spinner,
  Container
} from "reactstrap";
import { api, UPLOAD_TIMEOUT_MS } from "services/api";
import { downloadAuthenticatedFile } from "services/downloadFile";
import NotifyComponent, { showNotify } from "components/notifications/notify";
import { describeApiError } from "services/apiErrorMessage";
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog';
import "./styles/rpa-import.scss";

const ImportRPA = () => {
  const [file, setFile] = useState(null);
  const [historicoParaExcluir, setHistoricoParaExcluir] = useState(null);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const notifyRef = useRef(null);
  const fileInputRef = useRef(null);

  const fetchFiles = async () => {
    try {
      const response = await api.get("/rpa/import-files/");
      setData(response.data);
    } catch (error) {
      console.error("Erro ao carregar arquivos:", error);
    }
  };

  useEffect(() => {
    fetchFiles();
  }, []);

  // Handlers para Drag & Drop
  const onDragOver = useCallback((e) => {
    e.preventDefault();
    setDragging(true);
  }, []);

  const onDragLeave = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
  }, []);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const droppedFile = e.dataTransfer.files[0];
      const validTypes = [".xlsx", ".xls"];
      const isExcel = validTypes.some(type => droppedFile.name.toLowerCase().endsWith(type));
      
      if (isExcel) {
        setFile(droppedFile);
      } else {
        showNotify(notifyRef, "Aviso", "Apenas arquivos Excel (.xlsx, .xls) são permitidos.", "warning");
      }
    }
  }, []);

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const removeFile = (e) => {
    e.stopPropagation();
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleUpload = async () => {
    if (!file) return showNotify(notifyRef, "Aviso", "Por favor, selecione um arquivo primeiro.", "warning");

    const formData = new FormData();
    formData.append("file", file);

    try {
      setLoading(true);
      await api.post("/rpa/import-files/", formData, { timeout: UPLOAD_TIMEOUT_MS });
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      fetchFiles();
      showNotify(notifyRef, "Sucesso", "Arquivo enviado com sucesso!", "success");
    } catch (error) {
      console.error(error);
      const response = error?.response?.data;
      if (response?.missing_columns) {
        showNotify(notifyRef, "Aviso", `Arquivo inválido.\n\nColunas obrigatórias ausentes:\n${response.missing_columns.join("\n")}`, "warning");
      } else if (response?.extra_columns) {
        showNotify(notifyRef, "Aviso", `Arquivo contém colunas não esperadas:\n\n${response.extra_columns.join("\n")}`, "warning");
      } else {
        showNotify(notifyRef, "Erro", describeApiError(error), "danger");
      }
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const statuses = {
      "Pendente Tratamento RPA": "warning",
      "Processando": "info",
      "Importando Telefones": "primary",
      "Processado": "success",
      "Erro": "danger",
    };
    return statuses[status] || "dark";
  };

  const handleDownload = (url, fallbackName) => {
    if (!url) return;
    downloadAuthenticatedFile(url, fallbackName).catch((err) => {
      console.error(err);
      showNotify(notifyRef, "Erro", "Erro ao baixar arquivo.", "danger");
    });
  };

  const handleDelete = (id) => {
    setHistoricoParaExcluir(id);
  };

  const handleConfirmDelete = async () => {
    if (historicoParaExcluir) {
      try {
        await api.delete(`/rpa/import-files/${historicoParaExcluir}/`);
        fetchFiles();
      } catch (error) {
        console.error(error);
        showNotify(notifyRef, "Erro", "Este arquivo não pode ser excluído no momento.", "danger");
      } finally {
        setHistoricoParaExcluir(null);
      }
    }
  };

  const handleCancelDelete = () => {
    setHistoricoParaExcluir(null);
  };

  return (
    <div className="content rpa-import-container">
      <ConfirmDialog
        isOpen={!!historicoParaExcluir}
        title="Atenção"
        message="Deseja realmente excluir este histórico?"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
      <NotifyComponent notifyRef={notifyRef} />
      <Container fluid>
        <Row className="mb-4 align-items-center">
          <Col>
            <h2 className="mb-0 text-dark" style={{ fontWeight: "700" }}>
            <i className="ni ni-cloud-upload-96 text-primary mr-2" />
            Importação de Precatórios
          </h2>
        </Col>
      </Row>

      <Row>
        <Col lg="8">
          <Card className="shadow-sm border-0 mb-4">
            <CardHeader className="bg-transparent pb-0">
              <h4 className="mb-0 text-muted font-weight-600">Iniciar Nova Importação</h4>
            </CardHeader>
            <CardBody>
              <div 
                className={`dropzone-container ${dragging ? "dragging" : ""} ${file ? "has-file" : ""} ${loading ? "is-loading-anim" : ""}`}
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
                onDrop={onDrop}
                onClick={() => !loading && fileInputRef.current.click()}
              >
                <input 
                  type="file" 
                  accept=".xlsx, .xls"
                  ref={fileInputRef} 
                  onChange={handleFileSelect}
                  style={{ display: "none" }}
                />
                
                <div className="upload-icon">
                  {loading ? (
                    <Spinner style={{ width: "3rem", height: "3rem", color: "#C2641A" }} />
                  ) : (
                    <i className={`ni ${file ? "ni-check-bold text-success" : "ni-cloud-upload-96"}`} />
                  )}
                </div>

                <div className="upload-text">
                  {!file ? (
                    <>
                      <h4>Arraste seu arquivo Excel aqui</h4>
                      <p>Ou clique para navegar entre seus documentos</p>
                    </>
                  ) : (
                    <>
                      <h4 className="text-success">Arquivo Selecionado</h4>
                      <p>Pronto para enviar</p>
                    </>
                  )}
                </div>
              </div>

              {file && (
                <div className="file-preview">
                  <div className="file-info">
                    <i className="ni ni-single-copy-04" />
                    <div>
                      <span className="name">{file.name}</span>
                      <span className="size">{(file.size / 1024).toFixed(2)} KB</span>
                    </div>
                  </div>
                  <Button 
                    color="link" 
                    className="text-danger p-0" 
                    onClick={removeFile}
                    disabled={loading}
                  >
                    <i className="ni ni-fat-remove mr-1" />
                    Remover
                  </Button>
                </div>
              )}

              {file && (
                <Button
                  color="primary"
                  block
                  disabled={loading}
                  onClick={handleUpload}
                  className="mt-4 py-3 font-weight-bold shadow-none"
                  style={{ borderRadius: "0.5rem" }}
                >
                  {loading ? "Processando envio..." : "Confirmar e Enviar Arquivo"}
                </Button>
              )}
            </CardBody>
          </Card>

          <Card className="shadow-sm border-0">
            <CardHeader className="bg-transparent border-0 d-flex justify-content-between align-items-center">
              <h4 className="mb-0 text-muted font-weight-600">Arquivos Enviados Recentemente</h4>
              <Button color="link" size="sm" onClick={fetchFiles}><i className="ni ni-refresh-02" /></Button>
            </CardHeader>
            <CardBody className="p-0">
              <Table className="align-items-center table-flush table-files" responsive>
                <thead className="thead-light">
                  <tr>
                    <th scope="col" className="px-4">ID</th>
                    <th scope="col">Arquivo</th>
                    <th scope="col">Status</th>
                    <th scope="col">Data/Hora</th>
                    <th scope="col" className="text-right px-4">Ação</th>
                  </tr>
                </thead>
                <tbody>
                  {data.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="text-center py-5">
                        <i className="ni ni-folder-17 display-4 text-muted mb-3" />
                        <p className="text-muted">Nenhum arquivo enviado até o momento.</p>
                      </td>
                    </tr>
                  ) : (
                    data.map((item) => (
                      <tr key={item.id}>
                        <td className="px-4 text-muted text-xs">#{item.id}</td>
                        <td>
                          <div className="d-flex align-items-center">
                            <span className="font-weight-600 text-sm">{item.original_name}</span>
                          </div>
                          <small className="text-muted">Por: {item.user_name}</small>
                        </td>
                        <td>
                          <span className={`badge badge-pill badge-${getStatusColor(item.status)}`} style={{ padding: "5px 12px" }}>
                            {item.status}
                          </span>
                        </td>
                        <td className="text-muted text-xs">
                          {new Date(item.request_date).toLocaleDateString()} <br />
                          {new Date(item.request_date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </td>
                        <td className="text-right px-4">
                          <div className="d-flex justify-content-end align-items-center" style={{ gap: "8px" }}>
                            {item.input_file && (
                              <Button color="primary" outline size="sm" onClick={() => handleDownload(item.input_file, item.original_name)} className="btn-action shadow-none">
                                Input
                              </Button>
                            )}
                            {item.output_file && (
                              <Button color="success" outline size="sm" onClick={() => handleDownload(item.output_file, item.original_name)} className="btn-action shadow-none">
                                Output
                              </Button>
                            )}
                            <Button color="link" size="sm" className="text-danger p-0 ml-2" onClick={() => handleDelete(item.id)}>
                              <i className="ni ni-trash-2" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </CardBody>
          </Card>
        </Col>

        <Col lg="4">
          <Card className="shadow-sm border-0 instructions-card h-100">
            <CardHeader className="bg-transparent pb-0">
              <h4 className="mb-0 text-muted font-weight-600 text-uppercase" style={{ fontSize: "0.8rem" }}>Guia de Importação</h4>
            </CardHeader>
            <CardBody>
              <div className="instruction-item">
                <div className="icon-circle">01</div>
                <div className="text">
                  <h5>Formato do Arquivo</h5>
                  <p>O sistema processa exclusivamente arquivos Excel (XLSX ou XLS).</p>
                </div>
              </div>
              <div className="instruction-item">
                <div className="icon-circle">02</div>
                <div className="text">
                  <h5>Estrutura de Colunas</h5>
                  <p>Certifique-se de que a planilha segue o modelo padrão do Syspax para evitar erros de validação.</p>
                </div>
              </div>
              <div className="instruction-item">
                <div className="icon-circle">03</div>
                <div className="text">
                  <h5>Processamento RPA</h5>
                  <p>Após o envio, o robô processará os dados em segundo plano. Você pode acompanhar o status na lista ao lado.</p>
                </div>
              </div>

              <Alert color="primary" className="mt-4 border-0 shadow-none" style={{ borderRadius: "0.5rem" }}>
                <div className="d-flex align-items-center">
                  <i className="ni ni-bulb-61 text-white mr-2" style={{ fontSize: "1.2rem" }} />
                  <span className="text-white text-xs">
                    <strong>Dica:</strong> Em caso de erro, exporte o "Input" para verificar as colunas que falharam na validação.
                  </span>
                </div>
              </Alert>
            </CardBody>
          </Card>
        </Col>
      </Row>
      </Container>
    </div>
  );
};

export default ImportRPA;